import { useEffect, useRef, useState } from 'react';
import {
  motion,
  useMotionValue,
  useSpring,
  useReducedMotion,
} from 'framer-motion';

/**
 * Curseur custom doré pour les pages publiques.
 *
 * - Dot 8 px qui suit la souris (avec spring pour fluidité).
 * - S'agrandit (ring 36 px) au hover sur les éléments interactifs
 *   (`a`, `button`, `[role="button"]`, `[data-cursor="hover"]`).
 * - Désactivé automatiquement sur :
 *   - `pointer:coarse` (tactile) — détection native via media query
 *   - `prefers-reduced-motion: reduce`
 * - Le curseur natif est CONSERVÉ partout : ce dot vit en surcouche
 *   (`pointer-events-none`) pour ne casser ni l'a11y ni le trackpad.
 *
 * Stabilité : les listeners `window` ne sont attachés QU'UNE FOIS quand
 * isFinePointer + non-reduced-motion sont stables. `mouseX`/`mouseY` sont
 * accédés via leur méthode imperative `.set()` et donc pas en deps.
 * `isVisible`/`isHovering` sont lus via refs pour éviter de re-attacher
 * les listeners à chaque move.
 */
export default function CursorDot() {
  const prefersReducedMotion = useReducedMotion();
  const [isFinePointer, setIsFinePointer] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isHovering, setIsHovering] = useState(false);

  // Refs miroirs : permettent aux handlers de lire l'état actuel sans être
  // re-créés à chaque update — donc effect deps minimal.
  const visibleRef = useRef(false);
  const hoveringRef = useRef(false);

  // raw mouse position (sans spring) — MotionValue stable entre renders
  const mouseX = useMotionValue(-100);
  const mouseY = useMotionValue(-100);

  // spring lissé qui suit la position (effet "lag" subtil)
  const x = useSpring(mouseX, { stiffness: 600, damping: 35, mass: 0.4 });
  const y = useSpring(mouseY, { stiffness: 600, damping: 35, mass: 0.4 });

  // Détection pointer:fine (souris / trackpad) — refresh si l'utilisateur
  // bascule entre un écran tactile et une souris (rare mais possible).
  useEffect(() => {
    const mql = window.matchMedia('(pointer: fine)');
    const update = () => setIsFinePointer(mql.matches);
    update();
    mql.addEventListener('change', update);
    return () => mql.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    if (!isFinePointer || prefersReducedMotion) return;

    const onMove = (e: MouseEvent) => {
      mouseX.set(e.clientX);
      mouseY.set(e.clientY);
      if (!visibleRef.current) {
        visibleRef.current = true;
        setIsVisible(true);
      }
      // Hover detection inlined : un seul listener au lieu de deux.
      const target = e.target as HTMLElement | null;
      const interactive = !!target?.closest(
        'a, button, [role="button"], [data-cursor="hover"], input[type="button"], input[type="submit"], label[for]'
      );
      if (interactive !== hoveringRef.current) {
        hoveringRef.current = interactive;
        setIsHovering(interactive);
      }
    };
    const onLeave = () => {
      if (visibleRef.current) {
        visibleRef.current = false;
        setIsVisible(false);
      }
    };
    const onEnter = () => {
      if (!visibleRef.current) {
        visibleRef.current = true;
        setIsVisible(true);
      }
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseleave', onLeave);
    window.addEventListener('mouseenter', onEnter);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseleave', onLeave);
      window.removeEventListener('mouseenter', onEnter);
    };
    // Volontairement minimal : mouseX/mouseY sont des MotionValues stables
    // (référence inchangée entre renders), et visibleRef/hoveringRef sont
    // des refs qui ne déclenchent pas non plus de re-attache.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFinePointer, prefersReducedMotion]);

  if (!isFinePointer || prefersReducedMotion) return null;

  return (
    <>
      {/* Anneau qui s'agrandit au hover (smooth via spring) */}
      <motion.div
        className="pointer-events-none fixed top-0 left-0 z-[100] rounded-full border border-[#D4A574]/60 mix-blend-difference"
        style={{
          x,
          y,
          translateX: '-50%',
          translateY: '-50%',
          width: isHovering ? 36 : 28,
          height: isHovering ? 36 : 28,
          opacity: isVisible ? (isHovering ? 0.9 : 0.5) : 0,
          transition:
            'width 200ms ease, height 200ms ease, opacity 200ms ease',
        }}
        aria-hidden="true"
      />
      {/* Dot central plus rapide (pas de spring) pour un fix-point précis */}
      <motion.div
        className="pointer-events-none fixed top-0 left-0 z-[100] h-1.5 w-1.5 rounded-full bg-[#D4A574]"
        style={{
          x: mouseX,
          y: mouseY,
          translateX: '-50%',
          translateY: '-50%',
          opacity: isVisible ? 1 : 0,
          transition: 'opacity 200ms ease',
        }}
        aria-hidden="true"
      />
    </>
  );
}
